import { LoginRequestSchema, RegisterRequestSchema } from '@codeframe/api-contract';
import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { useLogin, useMe, useRegister } from '../api/hooks.js';
import { BrandMark } from '../components/AppShell.js';
import { errorMessage } from '../components/Page.js';
import { Alert, Button, Field, inputClass } from '../components/ui.js';
import s from './AuthPage.module.css';

function Hero() {
  return (
    <div className={s.hero}>
      <div className={s.brand}>
        <BrandMark />
        CODEFRAME
      </div>
      <div>
        <h2 className={s.tagline}>
          Your code is your <em>power</em>.
        </h2>
        <div className={s.code}>
          <div>
            <span className={s.kw}>if</span> mech.health {'<'} <span className={s.num}>30</span>:
          </div>
          <div>
            {'    '}mech.<span className={s.fn}>repair</span>()
          </div>
        </div>
        <div className={s.log}>
          <span>[T14] Health reached 27%</span>
          <span>
            [T15] <b>EMERGENCY REPAIR triggered</b> → Repair +25 → Health 52%
          </span>
        </div>
      </div>
      <span className="label">Learn real programming · Build mecha software · Fight with it</span>
    </div>
  );
}

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { data: me } = useMe();
  const login = useLogin();
  const register = useRegister();
  const [form, setForm] = useState({ username: '', email: '', password: '', login: '' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // New pilots go straight to their first lesson; returning pilots to the Hangar.
  if (me) return <Navigate to={register.isSuccess ? '/academy' : '/'} replace />;

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    setFieldErrors({});
    if (mode === 'register') {
      const parsed = RegisterRequestSchema.safeParse(form);
      if (!parsed.success) {
        setFieldErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
        return;
      }
      register.mutate(parsed.data);
    } else {
      const parsed = LoginRequestSchema.safeParse(form);
      if (!parsed.success) return;
      login.mutate(parsed.data);
    }
  };
  const mutation = mode === 'register' ? register : login;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className={s.wrap}>
      <Hero />
      <div className={s.formSide}>
        <div className={s.card}>
          <div>
            <span className="label">{mode === 'register' ? 'New pilot' : 'Pilot authentication'}</span>
            <h1>{mode === 'register' ? 'Enlist in the Academy' : 'Welcome back, pilot'}</h1>
          </div>
          {mutation.error && <Alert>{errorMessage(mutation.error)}</Alert>}
          <form onSubmit={submit} noValidate>
            {mode === 'register' ? (
              <>
                <Field label="Callsign" error={fieldErrors.username}>
                  <input className={inputClass} value={form.username} onChange={set('username')} autoComplete="username" autoFocus />
                </Field>
                <Field label="Email" error={fieldErrors.email}>
                  <input className={inputClass} type="email" value={form.email} onChange={set('email')} autoComplete="email" />
                </Field>
              </>
            ) : (
              <Field label="Callsign or email">
                <input className={inputClass} value={form.login} onChange={set('login')} autoComplete="username" autoFocus />
              </Field>
            )}
            <Field label="Password" error={fieldErrors.password}>
              <input
                className={inputClass}
                type="password"
                value={form.password}
                onChange={set('password')}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              />
            </Field>
            <Button type="submit" variant="primary" size="large" loading={mutation.isPending}>
              {mode === 'register' ? 'Create pilot' : 'Sign in'}
            </Button>
          </form>
          <div className={s.switch}>
            {mode === 'register' ? (
              <>
                Already enlisted? <Link to="/login">Sign in</Link>
              </>
            ) : (
              <>
                New to CODEFRAME? <Link to="/register">Create a pilot</Link> — no programming experience needed.
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
